import { styled } from '@mui/material/styles';
import MuiAccordion from '@mui/material/Accordion';
import Typography from '@mui/material/Typography';
import { AccordionDetails, AccordionSummary, Autocomplete, Box, Button, Chip, Container, Paper, Stack, TextField, useMediaQuery, useTheme } from '@mui/material';
import { useState } from 'react';
import { ExpandMoreRounded } from '@mui/icons-material';
import NavBar from '../component/nav-bar';
import NavBar2 from '../component/nav-bar2';
import AppLogo from '../assets/applogo.png';


// Custom styled Accordion
const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `2px solid #dce6e6`,
  borderRadius: '8px', // Rounded corners
  marginBottom: '8px', // Spacing between accordions
  transition: 'all 0.3s ease-in-out', // Smooth transition
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
  '&:hover': {
    boxShadow: theme.shadows[3], // Add shadow on hover
  },
}));

function MetricsCalculator() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [expanded, setExpanded] = useState('panel1');

    const genders = ['Male', 'Female'];
    const abilities = ['Normal', 'Athletic'];

    const handleChange = (panel) => (event, newExpanded) => {
        setExpanded(newExpanded ? panel : false);
    };

    // bmi
    const [bmiForm, setBmiForm] = useState({
        height: '',
        weight: '',
        result: '0.0',
        status: '',
    });

    const handleBmiInputChange = (e) => {
        const { name, value } = e.target;
        setBmiForm((prevState) => ({
        ...prevState,
        [name]: value,
        }));
        calculateBmi({ ...bmiForm, [name]: value });
    };

    const calculateBmi = (form) => {
        const height = parseFloat(form['height']);
        const weight = parseFloat(form['weight']);

        if (isNaN(height) || isNaN(weight) || height <= 0 || weight <= 0) {
        setBmiForm((prevState) => ({
            ...prevState,
            result: '0.0',
            status: '',
        }));

        return;
        }

        const bmi = (weight / Math.pow(height, 2)).toFixed(2);    

        let status = '';
        if (bmi < 18.5) {
        status = 'Under-Weight';
        } else if (bmi >= 18.5 && bmi < 25) {
        status = 'Normal';
        } else if (bmi >= 25 && bmi <= 30) {
        status = 'Overweight';
        } else if (bmi > 30 && bmi <= 35) {
        status = 'Moderate Obesity';
        } else if (bmi > 35) {
        status = 'Obesity';
        } else {
        status = 'Undefined';
        }

        setBmiForm((prevState) => ({
        ...prevState,
        result: bmi,
        status: status,
        }));
    };

    // wthr
    const [wthrForm, setWthrForm] = useState({
        waist: '',
        hip: '',
        sex: '',
        result: '0.0',
        status: '',
    });
    
    const handleWthrInputChange = (e) => {
        const { name, value } = e.target;
        setWthrForm((prevState) => ({
        ...prevState,
        [name]: value,
        }));
        calculateWthr({ ...wthrForm, [name]: value });
    };

    
    const calculateWthr = (form) => {
        const waist = parseFloat(form['waist']);
        const hip = parseFloat(form['hip']);
        const sex = form['sex'];

        if (isNaN(waist) || isNaN(hip) || waist <= 0 || hip <= 0) {
        setWthrForm((prevState) => ({
            ...prevState,
            result: '0.0',
            status: ''
        }));

        return;
        }

        const wthr = (waist / hip).toFixed(2);

        setWthrForm((prevState) => ({
        ...prevState,
        result: wthr,
        status: ''
        }));

        if (sex === '') {
        return;
        }

        let status = '';

        if (sex === 'Male') {
        if (wthr < 0.9) {
            status = 'Normal';
        }
        else {
            status = 'Risk of Cardiovasular Diseases';
        }
        }
        else {
        if (wthr < 0.85) {
            status = 'Normal';
        }
        else {
            status = 'Risk of Cardiovasular Diseases';
        }
        }
        
        setWthrForm((prevState) => ({
        ...prevState,
        status: status
        }));

    };

    // body fat index
    const [bfpForm, setBfpForm] = useState({
        triceps: '',
        biceps: '',
        suprailiac: '',
        ssf: '',
        age: '',
        sex: '',
        ability: '',
        result: '0.0',
        status: '',
    });

    const handleBfpInputChange = (e) => {
        const { name, value } = e.target;
        setBfpForm((prevState) => ({
        ...prevState,
        [name]: value,
        }));
        calculateBfp({ ...bfpForm, [name]: value });
    };

    function getConversionFactor(skinfold, age, sex) {
        const table = [
        {
            skinfold: 15,
            male: [
            [17, 29, 4.8], [30, 39, null], [40, 49, null], [50, Infinity, 12.6] 
            ],
            female: [
            [18, 29, 10.5], [30, 39, null], [40, 49, null], [50, Infinity, null] 
            ]
        },
        {
            skinfold: 20,
            male: [
            [17, 29, 8.1], [30, 39, 12.2], [40, 49, 12.2], [50, Infinity, 15.6] 
            ],
            female: [
            [18, 29, 14.1], [30, 39, 17.0], [40, 49, 19.8], [50, Infinity, 21.4] 
            ]
        },
        {
            skinfold: 25,
            male: [
            [17, 29, 10.5], [30, 39, 14.2], [40, 49, 15.0], [50, Infinity, 18.6] 
            ],
            female: [
            [18, 29, 18.8], [30, 39, 19.4], [40, 49, 22.2], [50, Infinity, 24.0] 
            ]
        },
        {
            skinfold: 30,
            male: [
            [17, 29, 12.9], [30, 39, 16.2], [40, 49, 17.7], [50, Infinity, 20.8] 
            ],
            female: [
            [18, 29, 19.5], [30, 39, 21.8], [40, 49, 24.5], [50, Infinity, 26.6] 
            ]
        },
        {
            skinfold: 35,
            male: [
            [17, 29, 14.7], [30, 39, 17.7], [40, 49, 19.8], [50, Infinity, 22.9] 
            ],
            female: [
            [18, 29, 21.9], [30, 39, 23.7], [40, 49, 26.4], [50, Infinity, 28.5] 
            ]
        },
        {
            skinfold: 40,
            male: [
            [17, 29, 16.4], [30, 39, 19.2], [40, 49, 21.4], [50, Infinity, 24.7] 
            ],
            female: [
            [18, 29, 23.4], [30, 39, 25.5], [40, 49, 28.2], [50, Infinity, 30.3] 
            ]
        },
        {
            skinfold: 45,
            male: [
            [17, 29, 17.7], [30, 39, 20.4], [40, 49, 23.0], [50, Infinity, 26.5] 
            ],
            female: [
            [18, 29, 25.0], [30, 39, 26.9], [40, 49, 29.6], [50, Infinity, 31.9] 
            ]
        },
        {
            skinfold: 50,
            male: [
            [17, 29, 19.0], [30, 39, 21.5], [40, 49, 24.6], [50, Infinity, 27.9] 
            ],
            female: [
            [18, 29, 26.5], [30, 39, 28.2], [40, 49, 31.0], [50, Infinity, 33.4] 
            ]
        },
        {
            skinfold: 55,
            male: [
            [17, 29, 20.1], [30, 39, 22.5], [40, 49, 25.9], [50, Infinity, 29.2] 
            ],
            female: [
            [18, 29, 27.8], [30, 39, 29.4], [40, 49, 32.1], [50, Infinity, 34.6] 
            ]
        },
        {
            skinfold: 60,
            male: [
            [17, 29, 21.2], [30, 39, 23.6], [40, 49, 27.1], [50, Infinity, 30.4]
            ],
            female: [
            [18, 29, 29.1], [30, 39, 30.6], [40, 49, 33.2], [50, Infinity, 35.7]
            ]
        },
        {
            skinfold: 65,
            male: [
            [17, 29, 22.2], [30, 39, 24.3], [40, 49, 28.2], [50, Infinity, 31.6]
            ],
            female: [
            [18, 29, 30.2], [30, 39, 31.6], [40, 49, 34.1], [50, Infinity, 36.7]
            ]
        },
        {
            skinfold: 70,
            male: [
            [17, 29, 23.1], [30, 39, 25.1], [40, 49, 29.3], [50, Infinity, 32.7]
            ],
            female: [
            [18, 29, 31.2], [30, 39, 32.5], [40, 49, 35.0], [50, Infinity, 37.7]
            ]
        },
        {
            skinfold: 75,
            male: [
            [17, 29, 24.0], [30, 39, 25.9], [40, 49, 30.3], [50, Infinity, 33.8]
            ],
            female: [
            [18, 29, 32.2], [30, 39, 33.4], [40, 49, 35.9], [50, Infinity, 38.7]
            ]
        },
        {
            skinfold: 80,
            male: [
            [17, 29, 24.8], [30, 39, 26.6], [40, 49, 31.2], [50, Infinity, 34.8]
            ],
            female: [
            [18, 29, 33.1], [30, 39, 34.3], [40, 49, 36.7], [50, Infinity, 39.8]
            ]
        },
        {
            skinfold: 85,
            male: [
            [17, 29, 25.6], [30, 39, 27.2], [40, 49, 32.1], [50, Infinity, 35.8]
            ],
            female: [
            [18, 29, 34.0], [30, 39, 35.8], [40, 49, 37.5], [50, Infinity, 40.4]
            ]
        },
        {
            skinfold: 90,
            male: [
            [17, 29, 26.2], [30, 39, 27.8], [40, 49, 33.0], [50, Infinity, 36.6]
            ],
            female: [
            [18, 29, 34.8], [30, 39, 36.5], [40, 49, 38.3], [50, Infinity, 41.2]
            ]
        },
        {
            skinfold: 95,
            male: [
            [17, 29, 26.9], [30, 39, 28.4], [40, 49, 33.7], [50, Infinity, 37.4]
            ],
            female: [
            [18, 29, 35.6], [30, 39, 37.2], [40, 49, 39.0], [50, Infinity, 41.9]
            ]
        },
        {
            skinfold: 100,
            male: [
            [17, 29, 27.6], [30, 39, 29.0], [40, 49, 34.4], [50, Infinity, 38.2]
            ],
            female: [
            [18, 29, 36.4], [30, 39, 37.9], [40, 49, 39.7], [50, Infinity, 42.6]
            ]
        },
        {
            skinfold: 105,
            male: [
            [17, 29, 28.2], [30, 39, 29.6], [40, 49, 35.1], [50, Infinity, 39.0]
            ],
            female: [
            [18, 29, 37.1], [30, 39, 38.6], [40, 49, 40.4], [50, Infinity, 43.3]
            ]
        },
        {
            skinfold: 110,
            male: [
            [17, 29, 28.8], [30, 39, 30.1], [40, 49, 35.8], [50, Infinity, 39.7]
            ],
            female: [
            [18, 29, 37.8], [30, 39, 39.1], [40, 49, 41.0], [50, Infinity, 43.9]
            ]
        },
        {
            skinfold: 115,
            male: [
            [17, 29, 29.4], [30, 39, 30.6], [40, 49, 36.4], [50, Infinity, 40.4]
            ],
            female: [
            [18, 29, 38.4], [30, 39, 39.6], [40, 49, 41.5], [50, Infinity, 44.5]
            ]
        },
        {
            skinfold: 120,
            male: [
            [17, 29, 30.0], [30, 39, 31.1], [40, 49, 37.0], [50, Infinity, 41.1]
            ],
            female: [
            [18, 29, 39.0], [30, 39, 40.1], [40, 49, 42.0], [50, Infinity, 45.1]
            ]
        },
        {
            skinfold: 125,
            male: [
            [17, 29, 30.5], [30, 39, 31.5], [40, 49, 37.6], [50, Infinity, 41.8]
            ],
            female: [
            [18, 29, 39.6], [30, 39, 40.6], [40, 49, 42.5], [50, Infinity, 45.7]
            ]
        },
        {
            skinfold: 130,
            male: [
            [17, 29, 31.0], [30, 39, 31.9], [40, 49, 38.2], [50, Infinity, 42.4]
            ],
            female: [
            [18, 29, 40.2], [30, 39, 41.1], [40, 49, 43.0], [50, Infinity, 46.2]
            ]
        },
        {
            skinfold: 135,
            male: [
            [17, 29, 31.5], [30, 39, 32.3], [40, 49, 38.7], [50, Infinity, 43.0]
            ],
            female: [
            [18, 29, 40.8], [30, 39, 41.6], [40, 49, 43.5], [50, Infinity, 46.7]
            ]
        },
        {
            skinfold: 140,
            male: [
            [17, 29, 32.0], [30, 39, 32.7], [40, 49, 39.2], [50, Infinity, 43.6]
            ],
            female: [
            [18, 29, 41.3], [30, 39, 42.1], [40, 49, 44.0], [50, Infinity, 47.2]
            ]
        },
        {
            skinfold: 145,
            male: [
            [17, 29, 32.5], [30, 39, 33.1], [40, 49, 39.7], [50, Infinity, 44.1]
            ],
            female: [
            [18, 29, 41.8], [30, 39, 42.6], [40, 49, 44.5], [50, Infinity, 47.7]
            ]
        },
        {
            skinfold: 150,
            male: [
            [17, 29, 32.9], [30, 39, 33.6], [40, 49, 40.2], [50, Infinity, 44.6]
            ],
            female: [
            [18, 29, 42.3], [30, 39, 43.1], [40, 49, 45.0], [50, Infinity, 48.2]
            ]
        },
        {
            skinfold: 155,
            male: [
            [17, 29, 33.3], [30, 39, 33.9], [40, 49, 40.7], [50, Infinity, 45.1]
            ],
            female: [
            [18, 29, 42.8], [30, 39, 43.6], [40, 49, 45.4], [50, Infinity, 48.7]
            ]
        },
        {
            skinfold: 160,
            male: [
            [17, 29, 33.7], [30, 39, 34.3], [40, 49, 41.2], [50, Infinity, 45.1]
            ],
            female: [
            [18, 29, 43.3], [30, 39, 44.0], [40, 49, 45.8], [50, Infinity, 49.2]
            ]
        },
        {
            skinfold: 165,
            male: [
            [17, 29, 34.1], [30, 39, 34.6], [40, 49, 41.6], [50, Infinity, null]
            ],
            female: [
            [18, 29, 43.7], [30, 39, 44.4], [40, 49, 46.2], [50, Infinity, 49.6]
            ]
        },
        {
            skinfold: 170,
            male: [
            [17, 29, 34.5], [30, 39, 34.8], [40, 49, 42.0], [50, Infinity, 46.1]
            ],
            female: [
            [18, 29, 44.1], [30, 39, 44.8], [40, 49, 46.6], [50, Infinity, 50.0]
            ]
        },
        {
            skinfold: 175,
            male: [
            [17, 29, 34.9], [30, 39, null], [40, 49, null], [50, Infinity, null]
            ],
            female: [
            [18, 29, null], [30, 39, null], [40, 49, 45.2], [50, Infinity, 47.0]
            ]
        },
        {
            skinfold: 180,
            male: [
            [17, 29, 35.3], [30, 39, null], [40, 49, null], [50, Infinity, null]
            ],
            female: [
            [18, 29, null], [30, 39, null], [40, 49, 45.6], [50, Infinity, 47.4]
            ]
        },
        {
            skinfold: 185,
            male: [
            [17, 29, 35.6], [30, 39, null], [40, 49, null], [50, Infinity, null]
            ],
            female: [
            [18, 29, null], [30, 39, null], [40, 49, 45.9], [50, Infinity, 47.8]
            ]
        },
        {
            skinfold: 190,
            male: [
            [17, 29, 35.9], [30, 39, null], [40, 49, null], [50, Infinity, null]
            ],
            female: [
            [18, 29, null], [30, 39, null], [40, 49, 46.2], [50, Infinity, 48.2]
            ]
        },
        {
            skinfold: 195,
            male: [
            [17, 29, null], [30, 39, null], [40, 49, null], [50, Infinity, null]
            ],
            female: [
            [18, 29, null], [30, 39, null], [40, 49, 46.5], [50, Infinity, 48.5]
            ]
        },
        {
            skinfold: 200,
            male: [
            [17, 29, null], [30, 39, null], [40, 49, null], [50, Infinity, null]
            ],
            female: [
            [18, 29, null], [30, 39, null], [40, 49, null], [50, Infinity, 48.8]
            ]
        },
        {
            skinfold: 205,
            male: [
            [17, 29, null], [30, 39, null], [40, 49, null], [50, Infinity, null]
            ],
            female: [
            [18, 29, null], [30, 39, null], [40, 49, null], [50, Infinity, 49.1]
            ]
        }
        ];

        function binarySearch() {
        if (skinfold <= 15) {
            return 0;
        }

        //binary search the age for the table key
        let left = 0, right = table.length - 1;

        while (left <= right) {
            const mid = parseInt(left + ((right - left) / 2));
            
            const curKey = table[mid]['skinfold'];

            if (curKey === skinfold) {
            return mid;
            }
            else if (curKey > skinfold) {
            right = mid - 1;
            }
            else {
            left = mid + 1;
            }
        }
        
        left--;

        // cases where the age is not in any of the available ranges
        if (left < 0 || left >= table.length) {
            return null;
        }

        return left;
        }

        const key = binarySearch();

        if (key === null) {
        return key;
        }
    
        if (sex === 'Male') {
        for (let index = 0; index < table[key]['male'].length; index++) {
            const pair = table[key]['male'][index]; 
            if (age >= pair[0] && age < pair[1]) {
            return pair[2];
            }
        }
        return null;
        }
        else {
        for (let index = 0; index < table[key]['female'].length; index++) {
            const pair = table[key]['female'][index];
            if (age >= pair[0] && age < pair[1]) {
            return pair[2];
            }
        }
        return null;
        }
    }
    
    const calculateBfp = (form) => {
        const triceps = parseFloat(form['triceps']);
        const biceps = parseFloat(form['biceps']);
        const suprailiac = parseFloat(form['suprailiac']);
        const ssf = parseFloat(form['ssf']);
        const age = parseInt(form['age']);
        const sex = form['sex'];
        const ability = form['ability'];

        if (isNaN(triceps) || isNaN(biceps) || isNaN(suprailiac) || isNaN(ssf) || isNaN(age) || !sex || !ability) {
        setBfpForm((prevState) => ({
            ...prevState,
            result: '0.0',
            status: ''
        }));

        return;
        }

        const skinfold = triceps + biceps + suprailiac + ssf;

        const cf = getConversionFactor(skinfold, age, sex);
        
        if (cf === null) {
        setBfpForm((prevState) => ({
            ...prevState,
            status: 'undefined'
        }));

        return;
        }
        
        let bfp = (skinfold * cf).toFixed(3);
        
        let status = 'undefined';

        if (sex === 'Male') {
        if (ability === 'Normal') {
            if (bfp >= 60 && bfp <= 80) {
            status = 'Excellent';    
            }
            else if (bfp >= 81 && bfp <= 90) {
            status = 'Good';
            }
            else if (bfp >= 91 && bfp <= 100) {
            status = 'Average';
            }
            else if (bfp >= 111 && bfp <= 150) {
            status = 'Below Average';
            }
            else if (bfp > 150) {
            status = 'Poor';
            }
        }  
        else {
            if (bfp >= 40 && bfp <= 60) {
            status = 'Excellent';    
            }
            else if (bfp >= 61 && bfp <= 80) {
            status = 'Good';
            }
            else if (bfp >= 81 && bfp <= 100) {
            status = 'Average';
            }
            else if (bfp >= 101 && bfp <= 130) {
            status = 'Below Average';
            }
            else if (bfp > 130) {
            status = 'Poor';
            }
        }
        }
        else {
        if (ability === 'Normal') {
            if (bfp >= 70 && bfp <= 90) {
            status = 'Excellent';    
            }
            else if (bfp >= 91 && bfp <= 100) {
            status = 'Good';
            }
            else if (bfp >= 101 && bfp <= 120) {
            status = 'Average';
            }
            else if (bfp >= 121 && bfp <= 150) {
            status = 'Below Average';
            }
            else if (bfp > 150) {
            status = 'Poor';
            }
        }
        else {
            if (bfp >= 50 && bfp <= 70) {
            status = 'Excellent';    
            }
            else if (bfp >= 71 && bfp <= 85) {
            status = 'Good';
            }
            else if (bfp >= 86 && bfp <= 110) {
            status = 'Average';
            }
            else if (bfp >= 111 && bfp <= 130) {
            status = 'Below Average';
            }
            else if (bfp > 130) {
            status = 'Poor';
            }
        }
        }

        setBfpForm((prevState) => ({
        ...prevState,
        result: `${bfp}%`,
        status: status
        }));
    };

    // food calorie
    const [foodCalorieForm, setFoodCalorieForm] = useState({
        carbohydrate: '',
        fiber: '',
        protein: '',
        fat: '',
        result: '0.0'
    });

    const handleFoodCalorieInputChange = (e) => {
        const { name, value } = e.target;
        setFoodCalorieForm((prevState) => ({
        ...prevState,
        [name]: value,
        }));
        calculateFoodCalorie({ ...foodCalorieForm, [name]: value });
    };

    const calculateFoodCalorie = (form) => {
        const carbohydrate = parseFloat(form['carbohydrate']);
        const fiber = parseFloat(form['fiber']);
        const protein = parseFloat(form['protein']);
        const fat = parseFloat(form['fat']);

        if (isNaN(carbohydrate) || isNaN(fiber) || isNaN(protein) || isNaN(fat)) {   
        setFoodCalorieForm((prevState) => ({
            ...prevState,
            result: '0.0'
        }));   
        return;
        }

        const result = (carbohydrate * 4) + (fiber * 4) + (protein * 4) + (fat * 9);

        setFoodCalorieForm((prevState) => ({
        ...prevState,
        result: `${result}Kcal || ${(result * 4.184).toFixed(2)}KJ`
        }));
    }
    

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} gap={5}>

            <NavBar2 svgLogo={<img src={AppLogo}/>}/>

            <Container sx={{}}>
                <Accordion expanded={expanded === 'panel1'} onChange={handleChange('panel1')} 
                slotProps={{ transition: { unmountOnExit: true } }}>
                <AccordionSummary aria-controls="panel1d-content" id="panel1d-header" expandIcon={<ExpandMoreRounded/>}>
                    <Typography component="span">Body Mass Index (BMI)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            backgroundColor: '#dce6e6',
                            padding: 1
                        }}
                        >
                        <Paper
                            sx={{
                                width: '100%',
                                maxWidth: isMobile ? '100%' : '50%',
                                background: 'none',
                                boxShadow: 'none'
                            }}
                            gap={3}
                        >
                            <TextField
                                name="height"
                                label="Height (m)"
                                variant="outlined"
                                type="number"
                                value={bmiForm['height']}
                                onChange={handleBmiInputChange}
                                fullWidth
                                sx={{ marginBottom: 2, backgroundColor: 'white' }}
                            />

                            <TextField
                                name="weight"
                                label="Weight (kg)"
                                variant="outlined"
                                type="number"
                                value={bmiForm['weight']}
                                onChange={handleBmiInputChange}
                                fullWidth
                                sx={{ marginBottom: 2, backgroundColor: 'white' }}
                            />
                    
                            <Box
                                sx={{
                                    backgroundColor: 'grey.500',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    borderRadius: 1,
                                    padding: 1,
                                    boxShadow: 'none',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography sx={{ flexGrow: 1, textAlign: 'center' }}>{bmiForm['result']}</Typography>

                                {
                                    bmiForm['status'] &&
                                    <Chip label={bmiForm['status']} sx={{ backgroundColor: '#fc6149', color: 'white' }}/>
                                }

                            </Box>
                        </Paper>
                    </Box>
                </AccordionDetails>
                </Accordion>
                
                <Accordion expanded={expanded === 'panel2'} onChange={handleChange('panel2')}
                slotProps={{ transition: { unmountOnExit: true } }}>
                <AccordionSummary aria-controls="panel2d-content" id="panel2d-header" expandIcon={<ExpandMoreRounded/>}>
                    <Typography component="span">Waist-To-Hip-Ratio (WTHR)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                    }}
                    >
                    <Paper
                        sx={{
                        padding: 1,
                        width: '100%',
                        maxWidth: isMobile ? '100%' : '50%',
                        border: '1px solid #46464f'
                        }}
                        gap={3}
                    >
                        <TextField
                        name="waist"
                        label="Waist (cm)"
                        variant="outlined"
                        type="number"
                        value={wthrForm['waist']}
                        onChange={handleWthrInputChange}
                        fullWidth
                        sx={{ marginBottom: 2 }}
                        />

                        <TextField
                        name="hip"
                        label="Hip (cm)"
                        variant="outlined"
                        type="number"
                        value={wthrForm['hip']}
                        onChange={handleWthrInputChange}
                        fullWidth
                        sx={{ marginBottom: 2 }}
                        />

                        <Autocomplete
                        id="gender-select-demo"
                        options={genders}
                        autoHighlight
                        getOptionLabel={(option) => option}
                        renderOption={(props, option) => (
                            <Box key={option} component="li" {...props}>
                            {option}
                            </Box>
                        )}
                        renderInput={(params) => (
                            <TextField
                            {...params}
                            label="Sex"
                            variant="outlined"
                            />
                        )}
                        sx={{ marginBottom: 2 }}
                        fullWidth
                        value={wthrForm['sex']}
                        onChange={(event, newValue) => {
                            setWthrForm((prevState) => ({
                            ...prevState,
                            sex: newValue || ''
                            }));
                            calculateWthr({ ...wthrForm, sex: newValue || '' });
                        }}
                        />


                        <Box
                        sx={{
                            backgroundColor: '#ced0d9',
                            display: 'flex',
                            flexDirection: 'row',
                            borderRadius: 1,
                            padding: 1,
                            boxShadow: 'none',
                            alignItems: 'center'
                        }}
                        >
                        <Typography sx={{ flexGrow: 1, textAlign: 'center' }}>{wthrForm['result']}</Typography>

                        {
                            wthrForm['status'] &&
                            <Chip label={wthrForm['status']} sx={{ backgroundColor: '#fc6149', color: 'white' }}/>
                        }

                        </Box>
                    </Paper>
                    

                    </Box>
                </AccordionDetails>
                </Accordion>

                <Accordion expanded={expanded === 'panel3'} onChange={handleChange('panel3')}
                slotProps={{ transition: { unmountOnExit: true } }}>
                <AccordionSummary aria-controls="panel3d-content" id="panel3d-header" expandIcon={<ExpandMoreRounded/>}>
                    <Typography component="span">Body Fat Percentage (BFP)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                    }}
                    >
                    <Paper
                        sx={{
                        padding: 1,
                        width: '100%',
                        maxWidth: isMobile ? '100%' : '50%',
                        border: '1px solid #46464f'
                        }}
                        gap={3}
                    >

                        <TextField
                        name="triceps"
                        label="Triceps(mm)"
                        variant="outlined"
                        type="number"
                        value={bfpForm['triceps']}
                        onChange={handleBfpInputChange}
                        fullWidth
                        sx={{ marginBottom: 2 }}
                        />

                        <TextField
                        name="biceps"
                        label="Biceps(mm)"
                        variant="outlined"
                        type="number"
                        value={bfpForm['biceps']}
                        onChange={handleBfpInputChange}
                        fullWidth
                        sx={{ marginBottom: 2 }}
                        />
                        
                        <TextField
                        name="suprailiac"
                        label="Suprailiac(mm)"
                        variant="outlined"
                        type="number"
                        value={bfpForm['suprailiac']}
                        onChange={handleBfpInputChange}
                        fullWidth
                        sx={{ marginBottom: 2 }}
                        />

                        <TextField
                        name="ssf"
                        label="Subscapular(mm)"
                        variant="outlined"
                        type="number"
                        value={bfpForm['ssf']}
                        onChange={handleBfpInputChange}
                        fullWidth
                        sx={{ marginBottom: 2 }}
                        />

                        <TextField
                        name="age"
                        label="Age"
                        variant="outlined"
                        type="number"
                        value={bfpForm['age']}
                        onChange={handleBfpInputChange}
                        fullWidth
                        sx={{ marginBottom: 2 }}
                        />

                        <Autocomplete
                        id="gender-select-demo"
                        options={genders}
                        autoHighlight
                        getOptionLabel={(option) => option}
                        renderOption={(props, option) => (
                            <Box key={option} component="li" {...props}>
                            {option}
                            </Box>
                        )}
                        renderInput={(params) => (
                            <TextField
                            {...params}
                            label="Sex"
                            variant="outlined"
                            />
                        )}
                        sx={{ marginBottom: 2 }}
                        fullWidth
                        value={bfpForm['sex']}
                        onChange={(event, newValue) => {
                            setBfpForm((prevState) => ({
                            ...prevState,
                            sex: newValue || ''
                            }));
                            calculateBfp({ ...bfpForm, sex: newValue || '' });
                        }}
                        />

                        <Autocomplete
                        id="ability-select-demo"
                        options={abilities}
                        autoHighlight
                        getOptionLabel={(option) => option}
                        renderOption={(props, option) => (
                            <Box key={option} component="li" {...props}>
                            {option}
                            </Box>
                        )}
                        renderInput={(params) => (
                            <TextField
                            {...params}
                            label="Ability"
                            variant="outlined"
                            />
                        )}
                        sx={{ marginBottom: 2 }}
                        fullWidth
                        value={bfpForm['ability']}
                        onChange={(event, newValue) => {
                            setBfpForm((prevState) => ({
                            ...prevState,
                            ability: newValue || ''
                            }));
                            calculateBfp({ ...bfpForm, ability: newValue || '' });
                        }}
                        />
                            
                        <Box
                        sx={{
                            backgroundColor: '#ced0d9',
                            display: 'flex',
                            flexDirection: 'row',
                            borderRadius: 1,
                            padding: 1,
                            boxShadow: 'none',
                            alignItems: 'center'
                        }}
                        >
                        <Typography sx={{ flexGrow: 1, textAlign: 'center' }}>{bfpForm['result']}</Typography>

                        {
                            bfpForm['status'] &&
                            <Chip label={bfpForm['status']} sx={{ backgroundColor: '#fc6149', color: 'white' }}/>
                        }

                        </Box>
                    </Paper>
                    </Box>
                </AccordionDetails>
                </Accordion>

                <Accordion expanded={expanded === 'panel4'} onChange={handleChange('panel4')}
                slotProps={{ transition: { unmountOnExit: true } }}>
                <AccordionSummary aria-controls="panel4d-content" id="panel4d-header" expandIcon={<ExpandMoreRounded/>}>
                    <Typography component="span">Calculating calorie (energy) in food (Atwater factor)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                    }}
                    >
                    <Paper
                        sx={{
                        padding: 1,
                        width: '100%',
                        maxWidth: isMobile ? '100%' : '50%',
                        border: '1px solid #46464f'
                        }}
                        gap={3}
                    >

                        <TextField
                        name="carbohydrate"
                        label="Cabohydrate(g)"
                        variant="outlined"
                        type="number"
                        value={foodCalorieForm['carbohydrate']}
                        onChange={handleFoodCalorieInputChange}
                        fullWidth
                        sx={{ marginBottom: 2 }}
                        />

                        <TextField
                        name="fiber"
                        label="Fiber(g)"
                        variant="outlined"
                        type="number"
                        value={foodCalorieForm['fiber']}
                        onChange={handleFoodCalorieInputChange}
                        fullWidth
                        sx={{ marginBottom: 2 }}
                        />

                        <TextField
                        name="protein"
                        label="Protein(g)"
                        variant="outlined"
                        type="number"
                        value={foodCalorieForm['protein']}
                        onChange={handleFoodCalorieInputChange}
                        fullWidth
                        sx={{ marginBottom: 2 }}
                        />

                        <TextField
                        name="fat"
                        label="Fat(g)"
                        variant="outlined"
                        type="number"
                        value={foodCalorieForm['fat']}
                        onChange={handleFoodCalorieInputChange}
                        fullWidth
                        sx={{ marginBottom: 2 }}
                        />

                        <Box
                        sx={{
                            backgroundColor: '#ced0d9',
                            display: 'flex',
                            flexDirection: 'row',
                            borderRadius: 1,
                            padding: 1,
                            boxShadow: 'none',
                            alignItems: 'center'
                        }}
                        >
                        <Typography sx={{ flexGrow: 1, textAlign: 'center' }}>{foodCalorieForm['result']}</Typography>
                        </Box>
                    </Paper>
                    </Box>
                </AccordionDetails>
                </Accordion>
            </Container>
        
        </Box>
    );
};
export default MetricsCalculator;